/**
 * Wraps protected routes with authentication check and shared layout.
 *
 * Behind featureFlags.globalShell: the new Sidebar + TopBar grid shell
 * (flag on, default), or the legacy Header stack (flag explicitly set to
 * false) — see the Global Shell spec (Web UI Redesign PRD 1). Some E2E
 * servers still pin the flag to false to keep their legacy-Header
 * assertions valid — see apps/web/src/utils/featureFlags.ts and
 * apps/web/e2e/README.md. useAuth/useSSEConnection/the JIT-enrollment
 * effect stay unconditional above the flag branch either way (B6.6) so
 * real-time + enrollment behave identically on both paths.
 */

import type { JSX } from 'react';
import { useEffect, useRef } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import { PageChromeProvider } from '../contexts/PageChromeContext';
import { useAuth } from '../hooks/useAuth';
import { useSSEConnection } from '../hooks/useSSEConnection';
import { recordLoginSession, triggerJitEnrollment } from '../services/api/organizations';
import { featureFlags } from '../utils/featureFlags';
import { logger } from '../utils/logger';

import { Footer } from './Footer';
import { Header } from './Header';
import { InvitationBanner } from './InvitationBanner';
import { LoadingSpinner } from './LoadingSpinner';
import { Sidebar } from './shell/Sidebar';
import { TopBar } from './shell/TopBar';

export function ProtectedLayout(): JSX.Element {
  const { loading, user } = useAuth();
  const enrolledUidRef = useRef<string | null>(null);

  // Initialize persistent SSE connection for authenticated users
  // Connects once on mount, stays connected, closes on logout/unmount
  useSSEConnection();

  // JIT domain enrollment — fire once per authenticated user session.
  // Idempotent on the backend: already-enrolled users get enrolled:false.
  useEffect(() => {
    if (user?.uid && user.uid !== enrolledUidRef.current) {
      enrolledUidRef.current = user.uid;
      void triggerJitEnrollment().catch(err => {
        logger.warn(
          { component: 'ProtectedLayout', error: String(err) },
          'JIT domain enrollment failed',
        );
      });
      void recordLoginSession();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.emailVerified) {
    return <Navigate to="/verify-email" state={{ email: user.email }} replace />;
  }

  if (!featureFlags.globalShell) {
    return (
      <PageChromeProvider>
        <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
          <Header />
          <InvitationBanner />
          <main className="flex-1 min-h-0 border-t border-default overflow-y-auto">
            <Outlet />
          </main>
          <Footer />
        </div>
      </PageChromeProvider>
    );
  }

  return (
    <PageChromeProvider>
      <div className="grid h-screen grid-cols-[auto_1fr] grid-rows-[1fr] overflow-hidden bg-background text-foreground">
        {/* First tab stop: visually hidden until focused, jumps past the sidebar/topbar to #main. */}
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
        >
          Skip to main content
        </a>

        {/* grid-rows-[1fr] above pins this row to h-screen's 100vh, but a grid item's default
            min-height:auto still lets it grow past that track to fit its content (the grid
            analogue of the flex min-h-0 issue below) — min-h-0 on both row items is what actually
            clips them to 100vh instead of silently overflowing the page by their content's excess
            height, which pushed Footer below the viewport with no way to scroll to it. */}
        <div className="hidden min-h-0 lg:block">
          <Sidebar />
        </div>

        <div className="grid min-h-0 min-w-0 grid-rows-[auto_1fr] overflow-hidden">
          <TopBar />
          {/* h-full (not just min-h-full) gives this column a definite height, so a route that opts
              itself out of shared sizing (its own root using h-full + overflow-hidden, e.g. a board
              with independently scrolling panes) has a real bound to size against instead of an
              auto/content-driven one — see apps/web/src/pages/Products/ProductView.tsx. main needs
              min-h-0 for the same reason: as a flex item its default min-height:auto would otherwise
              keep it content-sized regardless of the definite height now available above it.
              main scrolls its own content (rather than letting main's overflow render past its box):
              with min-h-0 capping main below its content's natural size but no overflow handling of
              main's own, the excess doesn't push Footer down — being in-flow siblings, Footer's position
              is set by main's box size, not its (visually-overflowing-but-not-flow-affecting) content —
              so Footer ends up pinned partway up the page with main's tail content rendering through it.
              Scoping the scroll to main keeps TopBar/InvitationBanner/Footer fixed and always reachable
              regardless of how tall the routed content is. */}
          <div className="overflow-y-auto">
            <div className="flex h-full min-h-full flex-col">
              <InvitationBanner />
              <main id="main" tabIndex={-1} className="min-h-0 flex-1 overflow-y-auto">
                <Outlet />
              </main>
              <Footer />
            </div>
          </div>
        </div>
      </div>
    </PageChromeProvider>
  );
}
